// Pure payload-building + validation for the consult order (ITAM-2583, ported
// for the ITAM-2887 advisor portal: the pharmacy is picked per draft, and the
// order is a pickup or a delivery). Extracted from React so the submission and
// validation logic is unit-testable without rendering. Consumed by
// useConsultOrderLifecycle (submit) and by ConsultOrderFormModal (summary).

import {
  ConsultCnCOrderRequest,
  CnCOrderDeliveryRequest,
  CnCOrderRequest,
  ConsultOrderItemRequest,
  ConsultOrderPrefillResponse,
  ConsultPharmacyPair,
  UserAddressRequestPayload,
} from "../types/consultOrder.types";
import { ConsultOrderDraft } from "../contexts/ConsultOrderContext";

export type ConsultOrderMarketplaceInfo = ConsultPharmacyPair;

export interface ConsultOrderValidationErrors {
  pharmacy?: string;
  items?: string;
  firstName?: string;
  phoneNumber?: string;
  address?: string;
  personalId?: string;
  deliveryTimeSlot?: string;
  delivery?: string;
  deliveryConsent?: string;
}

export interface ConsultOrderValidationResult {
  valid: boolean;
  errors: ConsultOrderValidationErrors;
}

export interface ConsultOrderValidationOptions {
  /**
   * Enforce the runtime delivery pre-checks (zone and fee) recorded on the
   * draft. The lifecycle submit sets it; pure-logic callers leave it off and
   * get the structural rules only.
   */
  enforcePrechecks?: boolean;
}

export const PHARMACY_REQUIRED_ERROR = "Izaberite apoteku.";
export const DELIVERY_CONSENT_ERROR =
  "Potvrdite saglasnost korisnika za pomoć pri preuzimanju.";

/**
 * Stable identity for a delivery zone selection: the picked pharmacy pair, the
 * delivery type and the resolved address coordinates. DeliverySection tags
 * each pre-check result with this key; validation recomputes it from the
 * CURRENT draft so a result whose key no longer matches (pharmacy, address or
 * type changed) is treated as unverified. The pair is part of the key because
 * the pharmacy can change under an unchanged address.
 */
export const deliveryZoneKey = (
  pair: ConsultPharmacyPair,
  fulfillment: ConsultOrderDraft["fulfillment"],
  lat: number,
  lng: number,
): string =>
  `${pair.marketplaceId}:${pair.branchMarketplaceId}:${fulfillment}:${lat}:${lng}`;

/**
 * Identity of a resolved delivery fee: the pharmacy pair (the fee is the
 * branch's own SERVICE item) and the delivery type (the price band).
 */
export const deliveryFeeKey = (
  pair: ConsultPharmacyPair,
  fulfillment: ConsultOrderDraft["fulfillment"],
): string =>
  `${pair.marketplaceId}:${pair.branchMarketplaceId}:${fulfillment}`;

/** Loose plausibility check for a Serbian-ish phone (no OTP / no verification claim). */
export const isPlausiblePhone = (raw: string): boolean => {
  const value = raw.trim();
  if (!/^[+]?[\d\s()/-]{6,20}$/.test(value)) {
    return false;
  }
  const digits = value.replace(/\D/g, "");
  return digits.length >= 6 && digits.length <= 15;
};

/**
 * Resolve the delivery address into a submittable payload, or null when the
 * current draft has no valid (non-zero lat/lng) address for its mode.
 */
export const resolveAddressPayload = (
  draft: ConsultOrderDraft,
  prefill: ConsultOrderPrefillResponse | null,
): UserAddressRequestPayload | null => {
  if (draft.addressMode === "NEW") {
    const a = draft.newAddress;
    if (!a || !a.name.trim() || !a.latitude || !a.longitude) {
      return null;
    }
    return a;
  }
  // SAVED
  if (draft.selectedAddressId == null || !prefill) {
    return null;
  }
  const found = prefill.addresses.find((x) => x.id === draft.selectedAddressId);
  if (!found || !found.latitude || !found.longitude) {
    return null;
  }
  return {
    id: found.id,
    name: found.name,
    number: found.number,
    apartment: found.apartment,
    floor: found.floor,
    region: found.region,
    city: found.city,
    postalCode: found.postalCode,
    latitude: found.latitude,
    longitude: found.longitude,
    otherInformation: found.otherInformation,
  };
};

/**
 * Validate the draft for submit-time gating: a picked pharmacy, at least one
 * item, a first name and a plausible phone; for a delivery also a resolvable
 * address, the identity document, a time slot (standard only) and the
 * caller's consent. Line quantities are clamped to [1, stock] by the context,
 * so they are not re-validated here.
 *
 * With `enforcePrechecks` the recorded zone and fee results are enforced too,
 * keyed on the CURRENT pharmacy pair, type and address:
 *   - zone: a missing or stale check blocks (still pending), a confirmed
 *     out-of-zone address blocks, an unknown zone (null: 403, missing pharmacy
 *     coordinates, failed detail) degrades to the backend's own check;
 *   - fee: a missing, stale or loading fee blocks, an unusable fee blocks, a
 *     resolved fee (a genuine 0 included) passes.
 */
export const validateConsultOrderDraft = (
  draft: ConsultOrderDraft,
  prefill: ConsultOrderPrefillResponse | null,
  options: ConsultOrderValidationOptions = {},
): ConsultOrderValidationResult => {
  const errors: ConsultOrderValidationErrors = {};
  const enforcePrechecks = options.enforcePrechecks === true;

  if (draft.pharmacy == null) {
    errors.pharmacy = PHARMACY_REQUIRED_ERROR;
  }
  if (draft.items.length < 1) {
    errors.items = "Dodajte bar jedan artikal.";
  }
  if (!draft.firstName.trim()) {
    errors.firstName = "Ime je obavezno.";
  }
  const phone = draft.phoneNumber.trim();
  if (!phone) {
    errors.phoneNumber = "Broj telefona je obavezan.";
  } else if (!isPlausiblePhone(phone)) {
    errors.phoneNumber = "Neispravan broj telefona.";
  }

  if (draft.fulfillment !== "PICKUP") {
    const addr = resolveAddressPayload(draft, prefill);
    if (!addr) {
      errors.address =
        "Izaberite ili unesite adresu za predaju sa koordinatama.";
    } else if (enforcePrechecks) {
      const check = draft.deliveryZoneCheck;
      const key = draft.pharmacy
        ? deliveryZoneKey(
            draft.pharmacy,
            draft.fulfillment,
            addr.latitude,
            addr.longitude,
          )
        : null;
      if (key == null || !check || check.key !== key) {
        errors.address =
          "Sačekajte proveru zone pomoći pri preuzimanju za izabranu adresu.";
      } else if (check.inZone === false) {
        errors.address =
          "Adresa je van zone pomoći pri preuzimanju za izabrani tip.";
      }
    }
    if (enforcePrechecks) {
      const fee = draft.deliveryServiceFee;
      const key = draft.pharmacy
        ? deliveryFeeKey(draft.pharmacy, draft.fulfillment)
        : null;
      if (key == null || !fee || fee.key !== key || fee.status === "loading") {
        errors.delivery = "Sačekajte učitavanje usluge rezervacije.";
      } else if (fee.status !== "ok" || typeof fee.price !== "number") {
        errors.delivery =
          "Usluga rezervacije za izabrani tip nije dostupna, nije moguće poslati.";
      }
    }
    if (!draft.personalId.trim()) {
      errors.personalId =
        "Lični identifikacioni broj je obavezan za pomoć pri preuzimanju.";
    }
    if (draft.fulfillment === "STANDARD_DELIVERY" && !draft.deliveryTimeSlot) {
      errors.deliveryTimeSlot = "Izaberite termin.";
    }
    if (!draft.deliveryConsent) {
      errors.deliveryConsent = DELIVERY_CONSENT_ERROR;
    }
  }

  return { valid: Object.keys(errors).length === 0, errors };
};

const samePharmacyPair = (
  a: ConsultPharmacyPair | null,
  b: ConsultPharmacyPair | null,
): boolean =>
  a === b ||
  (a != null &&
    b != null &&
    a.marketplaceId === b.marketplaceId &&
    a.branchMarketplaceId === b.branchMarketplaceId);

/**
 * Drop the submit-time field errors an edit made stale. Field errors are
 * computed on submit only (no live re-validation); once the advisor edits a
 * field, its error no longer describes the form and goes away until the next
 * submit recomputes everything. A pharmacy change drops every field error:
 * the zone, fee, time-slot and item errors all belong to the previous
 * pharmacy. Returns `errors` itself when nothing is dropped.
 */
export const dropEditedFieldErrors = (
  errors: ConsultOrderValidationErrors,
  prev: ConsultOrderDraft,
  next: ConsultOrderDraft,
): ConsultOrderValidationErrors => {
  if (prev === next || Object.keys(errors).length === 0) {
    return errors;
  }
  if (!samePharmacyPair(prev.pharmacy, next.pharmacy)) {
    return {};
  }
  // One entry per field key, so a new key cannot be added without deciding
  // which edit clears it.
  const edited: Record<
    Exclude<keyof ConsultOrderValidationErrors, "pharmacy">,
    boolean
  > = {
    items: prev.items !== next.items,
    address:
      prev.addressMode !== next.addressMode ||
      prev.selectedAddressId !== next.selectedAddressId ||
      prev.newAddress !== next.newAddress,
    delivery: prev.fulfillment !== next.fulfillment,
    deliveryTimeSlot: prev.deliveryTimeSlot !== next.deliveryTimeSlot,
    personalId:
      prev.personalIdType !== next.personalIdType ||
      prev.personalId !== next.personalId,
    deliveryConsent: prev.deliveryConsent !== next.deliveryConsent,
    firstName: prev.firstName !== next.firstName,
    phoneNumber: prev.phoneNumber !== next.phoneNumber,
  };
  const stale = (
    Object.keys(edited) as (keyof typeof edited)[]
  ).filter((key) => edited[key] && errors[key] != null);
  if (stale.length === 0) {
    return errors;
  }
  const kept = { ...errors };
  stale.forEach((key) => {
    delete kept[key];
  });
  return kept;
};

/**
 * Effective delivery fee for the draft: the resolved branch SERVICE-item
 * price, counted only when it was resolved OK for the CURRENT pharmacy and
 * fulfillment (the same key/status gate as validation: a fee resolved for
 * another pharmacy or type must not leak into the estimate while the
 * re-resolution is in flight). 0 for pickup / loading / stale / unresolved.
 */
export const draftDeliveryFee = (draft: ConsultOrderDraft): number => {
  if (draft.fulfillment === "PICKUP" || draft.pharmacy == null) {
    return 0;
  }
  const fee = draft.deliveryServiceFee;
  if (
    !fee ||
    fee.key !== deliveryFeeKey(draft.pharmacy, draft.fulfillment) ||
    fee.status !== "ok"
  ) {
    return 0;
  }
  return typeof fee.price === "number" ? fee.price : 0;
};

/**
 * Build the CnCOrderDeliveryRequest for the CURRENT (non-pickup) draft: the
 * RESOLVED SERVICE item id + price (from draft.deliveryServiceFee, never the
 * nullable marketplace fee), an explicit boolean saveAddress, deliveryTimeSlot
 * for STANDARD only. Never sends useFavouriteVolunteer, pensionCardNumber,
 * the deliveryFriend* fields or the UI-only consent. Throws when the draft has
 * no resolvable address (validation runs first, so this is defense in depth).
 */
export const buildDeliveryRequest = (
  draft: ConsultOrderDraft,
  prefill: ConsultOrderPrefillResponse,
): CnCOrderDeliveryRequest => {
  if (draft.fulfillment === "PICKUP") {
    throw new Error("buildDeliveryRequest called for a pickup draft.");
  }
  const address = resolveAddressPayload(draft, prefill);
  if (!address) {
    throw new Error("Delivery selected without a resolvable address.");
  }
  const isNewAddress = address.id == null;
  const resolvedFee = draft.deliveryServiceFee;
  const delivery: CnCOrderDeliveryRequest = {
    deliveryType: draft.fulfillment,
    deliveryPaymentType: draft.paymentType,
    serviceItemId: resolvedFee?.serviceItemId ?? null,
    userAddressRequest: address,
    personalIdType: draft.personalIdType,
    personalId: draft.personalId.trim(),
    // Resolved SERVICE-item price (a genuine 0 is valid); validation
    // guarantees a usable number before submit is allowed.
    deliveryPrice:
      typeof resolvedFee?.price === "number" ? resolvedFee.price : 0,
    // An existing (id-bearing) address never re-saves; a new address always
    // carries an explicit boolean (null would NPE the backend).
    saveAddress: isNewAddress ? draft.saveNewAddress : false,
  };
  const noteForDelivery = draft.noteForDelivery.trim();
  if (noteForDelivery) {
    delivery.noteForDelivery = noteForDelivery;
  }
  if (draft.fulfillment === "STANDARD_DELIVERY" && draft.deliveryTimeSlot) {
    delivery.deliveryTimeSlot = {
      start: draft.deliveryTimeSlot.start,
      end: draft.deliveryTimeSlot.end,
    };
  }
  return delivery;
};

/**
 * Build the exact ConsultCnCOrderRequest. Pickup omits cnCOrderDeliveryRequest
 * entirely; a delivery includes the buildDeliveryRequest result. Every item
 * carries gift:false. The pair is the picked pharmacy (the caller passes
 * `draft.pharmacy`).
 */
export const buildConsultOrderPayload = (
  draft: ConsultOrderDraft,
  prefill: ConsultOrderPrefillResponse,
  marketplaceInfo: ConsultOrderMarketplaceInfo,
): ConsultCnCOrderRequest => {
  const items: ConsultOrderItemRequest[] = draft.items.map((line) => ({
    itemId: line.item.id.itemId,
    quantity: line.quantity,
    gift: false as const,
  }));

  const cnCOrderRequest: CnCOrderRequest = {
    userId: prefill.user.id,
    marketplaceId: marketplaceInfo.marketplaceId,
    branchMarketplaceId: marketplaceInfo.branchMarketplaceId,
    userPhoneNumberForOrder: draft.phoneNumber.trim(),
  };

  const firstName = draft.firstName.trim();
  if (firstName) {
    cnCOrderRequest.firstName = firstName;
  }
  const secondName = draft.secondName.trim();
  if (secondName) {
    cnCOrderRequest.secondName = secondName;
  }
  const note = draft.note.trim();
  if (note) {
    cnCOrderRequest.note = note;
  }

  if (draft.fulfillment !== "PICKUP") {
    cnCOrderRequest.cnCOrderDeliveryRequest = buildDeliveryRequest(
      draft,
      prefill,
    );
  }

  return {
    consultId: prefill.consultId,
    cnCOrderRequest,
    items,
  };
};
